// IdTag — the house way to show an id. A raw id (esp. a uuid) the operator can't
// act on is noise; this makes every id USEFUL: the scannable short form is shown,
// the full id is on hover (title), and a copy button appears on hover to grab the
// full value. For an id that links to another entity, use a real nav link instead
// (this is for the entity's own reference id).
//
// WHY `copyable` EXISTS. The copy affordance is a real <button>, and a <button> may
// not contain another <button> — so an IdTag placed inside a clickable row or card
// produces invalid HTML and a validateDOMNesting warning on every render. Of the 126
// IdTags in the console, 11 sat inside one. Those pass `copyable={false}`: the id
// still shows, and the full value is still on hover, but the nested control is gone
// and the surrounding row keeps its own single, unambiguous click target.
//
// When the id IS the point of its cell and copying matters, put <CopyIdButton /> next
// to the clickable element instead of inside it — see reports/tabs/RunsTab.jsx.
import React from 'react';
import { Check, Copy } from 'lucide-react';
import { shortId } from '../lib/format';
import { toast , copyToClipboard } from '../lib/buses';

/**
 * The icon acknowledges the copy, instead of leaving the toast to do it alone.
 *
 * A toast appears away from the thing you clicked and is gone in a few seconds,
 * so on a row with several copyable values — an id, an endpoint, a webhook URL —
 * it tells you SOMETHING was copied but not which. The tick is on the control
 * you pressed, so there is no ambiguity, and it is driven by copyToClipboard's
 * return value: a browser that blocks the clipboard gets its warning and no
 * tick, rather than a confirmation of something that did not happen.
 *
 * Lives here so every copy affordance in the console behaves the same way.
 */
const useCopyAck = () => {
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1400);
    return () => clearTimeout(timer);
  }, [copied]);

  const run = React.useCallback(async (value, options) => {
    const ok = await copyToClipboard(value, options);
    if (ok) setCopied(true);
    return ok;
  }, []);

  return [copied, run];
};

/** The copy control on its own, for placing BESIDE a clickable element rather than in it. */
export const CopyIdButton = ({ value, className = '' }) => {
  const [copied, copy] = useCopyAck();
  if (value == null || value === '') return null;
  const full = String(value);
  return (
    <button
      type="button"
      // The row this sits next to is usually clickable too; without this the copy also
      // triggers the row's handler and the operator gets a navigation they didn't ask for.
      onClick={(e) => { e.stopPropagation(); copy(full, { label: 'ID copied', detail: full }); }}
      title="Copy full ID"
      className={`transition-colors focus:opacity-100 ${copied ? 'text-[#15803D]' : 'text-[#C5C2BC] hover:text-[#1F2937]'} ${className}`}
    >
      {copied ? <Check className="w-2.5 h-2.5" /> : <Copy className="w-2.5 h-2.5" />}
    </button>
  );
};

/**
 * A VALUE SHOWN IN FULL, WITH A COPY. IdTag's sibling for things that are not ids.
 *
 * IdTag shortens — right for a uuid, wrong for a slug, a URL or a reference, where the whole
 * string IS the useful thing and truncating it defeats the point. Same affordance otherwise:
 * the copy button appears on hover and goes through `copyToClipboard`, so a browser that blocks
 * the clipboard says so instead of claiming success (see lib/buses).
 *
 * `copyValue` exists because what you SHOW and what you PASTE are not always the same string —
 * a public link is displayed without its scheme to keep the row short, but pasting
 * "tkt.ug/domain/x" into a message gives you dead text, so the full https:// form is copied.
 */
// A value you can copy. When it is a web address it is ALSO a link: text that points somewhere
// opens when clicked — the copy button sits beside it rather than replacing the click. The link
// wears the house reference mark (ink + hairline rule, see EntityLink), never a paint-it-blue.
export const CopyValue = ({ value, copyValue, label = 'Copied', title, className = '', dash = '—' }) => {
  const [copied, copy] = useCopyAck();
  if (value == null || value === '') return <span className="text-[#9CA3AF]">{dash}</span>;
  const shown = String(value);
  const full = String(copyValue ?? value);
  const isUrl = /^https?:\/\/\S+$/.test(full);
  return (
    <span className={`inline-flex items-center gap-1.5 group/cv ${className}`} title={title || full}>
      {isUrl
        ? <a href={full} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="break-all underline decoration-[#D1CFC7] decoration-1 underline-offset-[3px] hover:decoration-[#1F2937] hover:text-[#1F2937]">{shown}</a>
        : <span className="break-all">{shown}</span>}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); copy(full, { label, detail: full }); }}
        title={`Copy ${full}`}
        // Stays visible while it is ticking: a confirmation that vanishes when
        // the pointer leaves has not confirmed anything.
        className={`transition-opacity focus:opacity-100 flex-shrink-0 ${copied ? 'text-[#15803D] opacity-100' : 'text-[#C5C2BC] hover:text-[#1F2937] opacity-0 group-hover/cv:opacity-100'}`}
      >
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      </button>
    </span>
  );
};

export const IdTag = ({ value, className = '', dash = '—', format, copyable = true }) => {
  const [copied, copy] = useCopyAck();
  if (value == null || value === '') return <span className="text-[#9CA3AF]">{dash}</span>;
  const full = String(value);
  // `format` only changes what's SHOWN (e.g. order codes dashed every 4); the raw
  // value is still what's copied and shown on hover, so search/paste stay correct.
  const shown = format ? format(full) : shortId(full);
  const onCopy = (e) => {
    e.stopPropagation();
    copy(full, { label: 'ID copied', detail: full });
  };
  return (
    <span className={`text-nowrap inline-flex items-center gap-1 font-mono group/id ${className}`} title={full}>
      {shown}
      {copyable && (
        <button type="button" onClick={onCopy} title="Copy full ID" className={`transition-opacity focus:opacity-100 ${copied ? 'text-[#15803D] opacity-100' : 'text-[#C5C2BC] hover:text-[#1F2937] opacity-0 group-hover/id:opacity-100'}`}>{copied ? <Check className="w-2.5 h-2.5" /> : <Copy className="w-2.5 h-2.5" />}</button>
      )}
    </span>
  );
};
